import type { BillingPaymentDto } from "api/types";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { currencySymbol } from "utilities";

import styles from "./InvoiceRowItem.module.css";

type InvoiceRowItemProps = {
	/** True while this row's invoice download is in flight. */
	isDownloading: boolean;
	onDownload: (paymentId: string) => void;
	payment: BillingPaymentDto;
};

const formatDate = (iso: string): string => {
	const date = new Date(iso);

	return Number.isNaN(date.getTime())
		? ""
		: date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Formats a charge in the currency it was actually taken in. Rows written before international
 * support carry no currency and are INR, which is what every one of them was — so the fallback is
 * the old behaviour exactly, and historical rows render unchanged.
 */
const formatAmount = (amount: number, currency?: string): string => {
	const code = (currency ?? "INR").toUpperCase();
	const locale = code === "INR" ? "en-IN" : "en-US";
	const fractionDigits = amount % 1 === 0 ? 0 : 2;

	return `${currencySymbol(code)}${amount.toLocaleString(locale, {
		maximumFractionDigits: 2,
		minimumFractionDigits: fractionDigits
	})}`;
};

/** One row of the billing history table: date, total, status, and the invoice download action. */
const InvoiceRowItem = ({ isDownloading, onDownload, payment }: InvoiceRowItemProps) => {
	const { t } = useTranslation("settingsPage");
	const onViewClick = useCallback((): void => onDownload(payment.id), [onDownload, payment.id]);

	return (
		<tr className={styles.row}>
			<td className={styles.cell}>{formatDate(payment.paidAtUtc)}</td>
			<td className={styles.cell}>{formatAmount(payment.amount, payment.currency)}</td>
			<td className={styles.cell}>{payment.status}</td>
			<td className={styles.cell}>
				{payment.invoiceAvailable ? (
					<button className={styles.viewLink} disabled={isDownloading} onClick={onViewClick} type="button">
						{isDownloading ? t("billing.downloading") : t("billing.view")}
					</button>
				) : (
					<span className={styles.noInvoice}>—</span>
				)}
			</td>
		</tr>
	);
};

export default InvoiceRowItem;
